/**
 * Live, speaker-separated transcription via AWS Transcribe Streaming.
 *
 * Browser captures microphone audio → AudioWorklet downsamples to 16 kHz
 * mono 16-bit PCM → frames are pushed onto an async iterable that the AWS
 * SDK uses for `StartStreamTranscriptionCommand`. Partial and final
 * utterances are surfaced via callbacks. Speaker labels (e.g. spk_0) are
 * passed through unchanged — the UI maps them to clinician/patient.
 *
 * STS credentials are obtained from our backend (`POST /v5/notes/transcribe-stream/credentials`).
 * No audio ever flows through our servers.
 */

import {
  TranscribeStreamingClient,
  StartStreamTranscriptionCommand,
} from "@aws-sdk/client-transcribe-streaming";
import type { StartStreamTranscriptionCommandInput } from "@aws-sdk/client-transcribe-streaming";
import type { TranscribeStreamCredentials } from "@/v5/services/notes.service";
import { DictationAudioSetupError } from "./dictationErrors";

export interface LiveTranscriptItem {
  id: string;
  isPartial: boolean;
  speakerLabel: string;
  text: string;
  startMs: number | null;
  endMs: number | null;
}

export interface TranscribeSessionOptions {
  credentials: TranscribeStreamCredentials;
  languageCode?: string;
  deviceId?: string;
  onItem: (item: LiveTranscriptItem) => void;
  onError?: (err: unknown) => void;
  onStatus?: (status: TranscribeSessionStatus) => void;
  /** The device actually granted, which may differ from the one requested. */
  onDeviceInUse?: (deviceId: string) => void;
}

export type TranscribeSessionStatus =
  | "requesting-mic"
  | "listening"
  | "stopping"
  | "stopped"
  | "error";

const TARGET_SAMPLE_RATE = 16000;

const WORKLET_SOURCE = `
  class PcmCapture extends AudioWorkletProcessor {
    process(inputs) {
      const input = inputs[0];
      if (!input || !input.length) return true;
      const channel = input[0];
      if (!channel) return true;
      // Copy because the underlying buffer is reused
      const out = new Float32Array(channel.length);
      out.set(channel);
      this.port.postMessage(out, [out.buffer]);
      return true;
    }
  }
  registerProcessor('pcm-capture', PcmCapture);
`;

function downsampleFloat32(input: Float32Array, fromRate: number, toRate: number): Float32Array {
  if (fromRate === toRate) return input;
  const ratio = fromRate / toRate;
  const newLen = Math.round(input.length / ratio);
  const out = new Float32Array(newLen);
  let offset = 0;
  for (let i = 0; i < newLen; i++) {
    const next = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    let count = 0;
    for (let j = offset; j < next; j++) {
      sum += input[j];
      count++;
    }
    out[i] = count > 0 ? sum / count : 0;
    offset = next;
  }
  return out;
}

function floatToInt16(input: Float32Array): Int16Array {
  const out = new Int16Array(input.length);
  for (let i = 0; i < input.length; i++) {
    const s = Math.max(-1, Math.min(1, input[i]));
    out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
  }
  return out;
}

export class TranscribeSession {
  private client: TranscribeStreamingClient | null = null;
  private audioContext: AudioContext | null = null;
  private mediaStream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private mediaSource: MediaStreamAudioSourceNode | null = null;
  private workletObjectUrl: string | null = null;
  /**
   * Which module URL the worklet loaded from, or null if neither worked.
   * Read on the failure path so a report says whether the same-origin asset was
   * refused and the blob fallback was reached.
   */
  workletLoadedFrom: "asset" | "blob" | null = null;

  // Async chunk queue → Transcribe SDK iterable
  private pendingResolve: ((v: IteratorResult<{ AudioEvent: { AudioChunk: Uint8Array } }, void>) => void) | null = null;
  private chunkQueue: Uint8Array[] = [];
  private closed = false;

  private stoppedExternally = false;

  constructor(private options: TranscribeSessionOptions) {}

  /**
   * Register the capture worklet, preferring the same-origin asset.
   *
   * `blob:` module URLs are refused by some extensions and managed-device
   * policies, which is why the bundled file is tried first. The blob remains as
   * a fallback for any environment where the asset URL cannot be resolved.
   * Whichever path is used is recorded so a failure report can say which.
   */
  private async loadWorklet(ctx: AudioContext): Promise<void> {
    try {
      const assetUrl = new URL("./pcmCapture.worklet.js", import.meta.url);
      await ctx.audioWorklet.addModule(assetUrl.href);
      this.workletLoadedFrom = "asset";
      return;
    } catch (assetErr) {
      console.warn(
        "Same-origin audio worklet failed to load; falling back to a blob URL.",
        assetErr
      );
    }

    const blob = new Blob([WORKLET_SOURCE], { type: "application/javascript" });
    this.workletObjectUrl = URL.createObjectURL(blob);
    await ctx.audioWorklet.addModule(this.workletObjectUrl);
    this.workletLoadedFrom = "blob";
  }

  async start(): Promise<void> {
    const { credentials, deviceId, languageCode = "en-US" } = this.options;
    this.options.onStatus?.("requesting-mic");

    // `ideal`, not `exact`: the picker auto-selects the first enumerated device
    // without the user choosing it, and device ids go stale when hardware is
    // unplugged or the browser rotates them. `exact` turns that into an
    // OverconstrainedError; `ideal` falls back to the system default, and the
    // caller reconciles its dropdown from `onDeviceInUse` below.
    const constraints: MediaStreamConstraints = {
      audio: deviceId
        ? { deviceId: { ideal: deviceId }, echoCancellation: true, noiseSuppression: true }
        : { echoCancellation: true, noiseSuppression: true },
      video: false,
    };
    const micPromise = navigator.mediaDevices.getUserMedia(constraints);
    // Cleared once the race settles — an uncleared timer still rejects after a
    // late-but-successful grant, surfacing as an unhandled rejection.
    let micTimer: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<never>((_, reject) => {
      micTimer = setTimeout(() => reject(new Error(
        "Microphone access timed out — please allow microphone access in your browser settings and try again."
      )), 10_000);
    });

    try {
      this.mediaStream = await Promise.race([micPromise, timeoutPromise]);
    } catch (err) {
      this.options.onStatus?.("error");
      this.options.onError?.(err);
      return;
    } finally {
      clearTimeout(micTimer);
    }

    // Report the device actually granted, which `ideal` may have redirected.
    const grantedId = this.mediaStream.getAudioTracks()[0]?.getSettings().deviceId;
    if (grantedId) this.options.onDeviceInUse?.(grantedId);

    // Everything from here to the worklet node is the browser building the audio
    // graph, and every step of it can be refused by a security policy or an
    // extension. Left unguarded these rejected `start()` with a raw DOMException
    // that the UI reported as a microphone-permission problem, which it never is.
    try {
      // Some browsers honor a sampleRate hint; if not, we downsample manually.
      const Ctor = (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext);
      try {
        this.audioContext = new Ctor({ sampleRate: TARGET_SAMPLE_RATE });
      } catch {
        this.audioContext = new Ctor();
      }
      if (this.audioContext.state === "suspended") {
        await this.audioContext.resume();
      }

      await this.loadWorklet(this.audioContext);

      this.mediaSource = this.audioContext.createMediaStreamSource(this.mediaStream);
      this.workletNode = new AudioWorkletNode(this.audioContext, "pcm-capture");
    } catch (err) {
      await this.cleanup();
      throw new DictationAudioSetupError(err, this.workletLoadedFrom);
    }

    const sourceRate = this.audioContext.sampleRate;
    this.workletNode.port.onmessage = (e: MessageEvent<Float32Array>) => {
      if (this.closed) return;
      const float32 = e.data;
      const downsampled = downsampleFloat32(float32, sourceRate, TARGET_SAMPLE_RATE);
      const int16 = floatToInt16(downsampled);
      const chunk = new Uint8Array(int16.buffer);
      this.pushChunk(chunk);
    };

    this.mediaSource.connect(this.workletNode);
    // Don't connect to destination — that would cause feedback.

    this.client = new TranscribeStreamingClient({
      region: credentials.region,
      credentials: {
        accessKeyId: credentials.accessKeyId,
        secretAccessKey: credentials.secretAccessKey,
        sessionToken: credentials.sessionToken,
      },
    });

    const commandInput: StartStreamTranscriptionCommandInput = {
      LanguageCode: languageCode as StartStreamTranscriptionCommandInput["LanguageCode"],
      MediaSampleRateHertz: TARGET_SAMPLE_RATE,
      MediaEncoding: "pcm",
      ShowSpeakerLabel: true,
      EnablePartialResultsStabilization: true,
      PartialResultsStability: "medium",
      AudioStream: this.audioStream(),
    };
    const command = new StartStreamTranscriptionCommand(commandInput);

    this.options.onStatus?.("listening");

    try {
      const response = await this.client.send(command);
      if (response.TranscriptResultStream) {
        for await (const event of response.TranscriptResultStream) {
          if (event.TranscriptEvent?.Transcript?.Results) {
            for (const result of event.TranscriptEvent.Transcript.Results) {
              this.handleResult(result);
            }
          }
        }
      }
    } catch (err) {
      if (!this.stoppedExternally) {
        this.options.onStatus?.("error");
        this.options.onError?.(err);
      }
    } finally {
      this.options.onStatus?.("stopped");
      await this.cleanup();
    }
  }

  async stop(): Promise<void> {
    if (this.closed) return;
    this.stoppedExternally = true;
    this.options.onStatus?.("stopping");
    this.closed = true;
    if (this.pendingResolve) {
      this.pendingResolve({ value: undefined, done: true });
      this.pendingResolve = null;
    }
  }

  private async cleanup(): Promise<void> {
    try { this.workletNode?.disconnect(); } catch { /* noop */ }
    try { this.mediaSource?.disconnect(); } catch { /* noop */ }
    try { this.mediaStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { await this.audioContext?.close(); } catch { /* noop */ }
    if (this.workletObjectUrl) URL.revokeObjectURL(this.workletObjectUrl);
    try { this.client?.destroy(); } catch { /* noop */ }
    this.workletNode = null;
    this.mediaSource = null;
    this.mediaStream = null;
    this.audioContext = null;
    this.workletObjectUrl = null;
    this.client = null;
  }

  private pushChunk(chunk: Uint8Array): void {
    if (this.pendingResolve) {
      const resolve = this.pendingResolve;
      this.pendingResolve = null;
      resolve({ value: { AudioEvent: { AudioChunk: chunk } }, done: false });
    } else {
      this.chunkQueue.push(chunk);
    }
  }

  private audioStream(): AsyncIterable<{ AudioEvent: { AudioChunk: Uint8Array } }> {
    const self = this;
    return {
      [Symbol.asyncIterator](): AsyncIterator<{ AudioEvent: { AudioChunk: Uint8Array } }> {
        return {
          next(): Promise<IteratorResult<{ AudioEvent: { AudioChunk: Uint8Array } }, void>> {
            if (self.chunkQueue.length > 0) {
              const chunk = self.chunkQueue.shift()!;
              return Promise.resolve({ value: { AudioEvent: { AudioChunk: chunk } }, done: false });
            }
            if (self.closed) {
              return Promise.resolve({ value: undefined, done: true });
            }
            return new Promise((resolve) => { self.pendingResolve = resolve; });
          },
        };
      },
    };
  }

  private handleResult(result: {
    ResultId?: string;
    IsPartial?: boolean;
    StartTime?: number;
    EndTime?: number;
    Alternatives?: { Transcript?: string; Items?: { Speaker?: string; Content?: string; Type?: string }[] }[];
  }): void {
    const alt = result.Alternatives?.[0];
    if (!alt || !alt.Transcript) return;

    const speakerLabel = (() => {
      const items = alt.Items ?? [];
      for (const it of items) {
        if (it.Speaker && it.Type !== "punctuation") return it.Speaker;
      }
      return "";
    })();

    this.options.onItem({
      id: result.ResultId ?? `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      isPartial: !!result.IsPartial,
      speakerLabel,
      text: alt.Transcript,
      startMs: typeof result.StartTime === "number" ? Math.round(result.StartTime * 1000) : null,
      endMs: typeof result.EndTime === "number" ? Math.round(result.EndTime * 1000) : null,
    });
  }
}

/**
 * Lists user-selectable input devices for the mic picker. Empty labels are
 * normal until the page has been granted mic permission once; we surface a
 * generic name in that case so the picker still has something to show.
 */
export async function listAudioInputs(): Promise<{ deviceId: string; label: string }[]> {
  if (!navigator.mediaDevices?.enumerateDevices) return [];
  let devices = await navigator.mediaDevices.enumerateDevices();
  const audioDevices = devices.filter((d) => d.kind === "audioinput");
  // Browsers hide labels/real IDs until mic permission is granted.
  // If we detect that situation, request permission briefly then re-enumerate.
  if (audioDevices.length > 0 && audioDevices.every((d) => !d.label)) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch {
      // Permission denied — fall through with generic labels
    }
  }
  return devices
    // Drop entries without a deviceId. Browsers return empty deviceIds
    // when the page lacks microphone permission; rendering them into a
    // Radix <Select.Item value="" /> crashes the New Note modal.
    .filter((d) => d.kind === "audioinput" && d.deviceId)
    .map((d, idx) => ({
      deviceId: d.deviceId,
      label: d.label || `Microphone ${idx + 1}`,
    }));
}
